import { supabase } from '@/lib/supabase';
import { UnifiedEpisode, UnifiedEpisodeTranslation } from '@/types/database';
import { withSupabaseResult } from '@/utils/supabase/withSupabaseResult';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const uniLog = createScopedLogger('UnifiedContent');

export interface UnifiedEpisodeWithTranslation {
  episode_id: number;
  series_id: number;
  season_id: number;
  season_position: number;
  episode_position: number;
  duration: number | null;
  product_year: number | null;
  url_streaming_no_drm: string | null;
  url_streaming_drm: string | null;
  title: string;
  description: string | null;
  language_used: string;
  created_at: string;
  updated_at: string;
}

export interface UnifiedSearchResult {
  episode_id: number;
  series_id: number;
  title: string;
  description: string | null;
  season_position: number;
  episode_position: number;
  duration: number | null;
}

class UnifiedContentService {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000;
  private readonly SUPABASE_BATCH_SIZE = 1000;
  private readonly IN_FILTER_BATCH_SIZE = 300;

  private getCacheKey(...parts: (string | number | null | undefined)[]): string {
    return parts.filter(p => p != null).join(':');
  }

  private async fetchAllRowsPaginated<T>(
    queryBuilder: () => any,
    context: string
  ): Promise<T[]> {
    const allResults: T[] = [];
    let offset = 0;
    let batchNumber = 0;
    const startTime = Date.now();

    console.log(`[UNIFIED-PAGINATION] Starting paginated fetch for ${context}...`);

    while (true) {
      batchNumber++;
      const batchStartTime = Date.now();

      const query = queryBuilder();
      const { data, error } = await query.range(offset, offset + this.SUPABASE_BATCH_SIZE - 1);

      if (error) {
        console.error(`[UNIFIED-PAGINATION] Error in batch ${batchNumber} for ${context}:`, error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log(`[UNIFIED-PAGINATION] Batch ${batchNumber}: 0 rows (end of data)`);
        break;
      }

      const batchDuration = Date.now() - batchStartTime;
      console.log(`[UNIFIED-PAGINATION] Batch ${batchNumber}: ${data.length} rows fetched (${batchDuration}ms)`);

      allResults.push(...data);

      if (data.length < this.SUPABASE_BATCH_SIZE) {
        break;
      }

      offset += this.SUPABASE_BATCH_SIZE;
    }

    const totalDuration = Date.now() - startTime;
    console.log(`[UNIFIED-PAGINATION] Completed ${context}: ${allResults.length} total rows in ${batchNumber} batch(es) (${totalDuration}ms)`);

    return allResults;
  }

  private async fetchInBatches<T>(
    ids: number[],
    queryBuilder: (batchIds: number[]) => any,
    context: string
  ): Promise<T[]> {
    if (ids.length <= this.IN_FILTER_BATCH_SIZE) {
      const { data, error } = await queryBuilder(ids);
      if (error) {
        console.error(`[UNIFIED-BATCH] Error fetching ${context}:`, error);
        throw error;
      }
      return data || [];
    }

    const allResults: T[] = [];
    const totalBatches = Math.ceil(ids.length / this.IN_FILTER_BATCH_SIZE);
    console.log(`[UNIFIED-BATCH] Splitting ${ids.length} IDs into ${totalBatches} batches for ${context}`);

    for (let i = 0; i < ids.length; i += this.IN_FILTER_BATCH_SIZE) {
      const batchIds = ids.slice(i, i + this.IN_FILTER_BATCH_SIZE);
      const batchNumber = Math.floor(i / this.IN_FILTER_BATCH_SIZE) + 1;

      const { data, error } = await queryBuilder(batchIds);

      if (error) {
        console.error(`[UNIFIED-BATCH] Error in batch ${batchNumber}/${totalBatches} for ${context}:`, error);
        throw error;
      }

      if (data) {
        allResults.push(...data);
      }

      console.log(`[UNIFIED-BATCH] Batch ${batchNumber}/${totalBatches}: ${data?.length || 0} rows`);
    }

    console.log(`[UNIFIED-BATCH] Completed ${context}: ${allResults.length} total rows from ${totalBatches} batches`);
    return allResults;
  }

  private getFromCache<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() - cached.timestamp > this.CACHE_TTL) {
      this.cache.delete(key);
      return null;
    }

    return cached.data as T;
  }

  private setCache<T>(key: string, data: T): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  clearCache(): void {
    this.cache.clear();
  }

  async getEpisodeById(
    episodeId: number,
    languageCode: string = 'en'
  ): Promise<UnifiedEpisodeWithTranslation | null> {
    if (!supabase) return null;

    const cacheKey = this.getCacheKey('episode', episodeId, languageCode);
    const cached = this.getFromCache<UnifiedEpisodeWithTranslation>(cacheKey);
    if (cached) return cached;

    const { data, error } = await withSupabaseResult<any[]>(
      supabase.rpc('get_unified_episode_with_translation', {
        p_episode_id: episodeId,
        p_language_code: languageCode
      }),
      { operation: 'unified.rpc.getEpisodeWithTranslation', logger: uniLog, fallback: [] }
    );

    if (error) {
      console.error('Error fetching unified episode:', error.message);
      return null;
    }

    const episode = data?.[0] || null;
    if (episode) {
      this.setCache(cacheKey, episode);
    }

    return episode;
  }

  async getEpisodesBySeason(
    seasonId: number,
    languageCode: string = 'en'
  ): Promise<UnifiedEpisodeWithTranslation[]> {
    if (!supabase) return [];

    const cacheKey = this.getCacheKey('season', seasonId, languageCode);
    const cached = this.getFromCache<UnifiedEpisodeWithTranslation[]>(cacheKey);
    if (cached) return cached;

    const { data, error } = await withSupabaseResult<any[]>(
      supabase.rpc('get_unified_episodes_by_season', {
        p_season_id: seasonId,
        p_language_code: languageCode
      }),
      { operation: 'unified.rpc.getEpisodesBySeason', logger: uniLog, fallback: [] }
    );

    if (error) {
      console.error('Error fetching episodes by season:', error.message);
      return [];
    }

    const episodes = data || [];
    this.setCache(cacheKey, episodes);

    return episodes;
  }

  async getEpisodesBySeries(
    seriesId: number,
    languageCode: string = 'en'
  ): Promise<UnifiedEpisodeWithTranslation[]> {
    if (!supabase) return [];

    const cacheKey = this.getCacheKey('series', seriesId, languageCode);
    const cached = this.getFromCache<UnifiedEpisodeWithTranslation[]>(cacheKey);
    if (cached) return cached;

    const { data, error } = await withSupabaseResult<any[]>(
      supabase.rpc('get_unified_episodes_by_series', {
        p_series_id: seriesId,
        p_language_code: languageCode
      }),
      { operation: 'unified.rpc.getEpisodesBySeries', logger: uniLog, fallback: [] }
    );

    if (error) {
      console.error('Error fetching episodes by series:', error.message);
      return [];
    }

    const episodes = data || [];
    this.setCache(cacheKey, episodes);

    return episodes;
  }

  async getFirstEpisodeOfSeries(
    seriesId: number,
    languageCode: string = 'en'
  ): Promise<UnifiedEpisodeWithTranslation | null> {
    if (!supabase) return null;

    const cacheKey = this.getCacheKey('first', seriesId, languageCode);
    const cached = this.getFromCache<UnifiedEpisodeWithTranslation>(cacheKey);
    if (cached) return cached;

    const { data, error } = await supabase
      .from('unified_contents_series_episodes')
      .select(`
        episode_id,
        series_id,
        season_id,
        season_position,
        episode_position,
        duration,
        product_year,
        url_streaming_no_drm,
        url_streaming_drm,
        created_at,
        updated_at
      `)
      .eq('series_id', seriesId)
      .order('season_position', { ascending: true })
      .order('episode_position', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error || !data) {
      console.error('Error fetching first episode:', error);
      return null;
    }

    const { data: translations } = await supabase
      .from('unified_contents_series_episodes_translations')
      .select('title, description, language_code')
      .eq('episode_id', data.episode_id)
      .in('language_code', [languageCode, 'en']);

    const preferredTranslation = translations?.find(t => t.language_code === languageCode);
    const englishTranslation = translations?.find(t => t.language_code === 'en');
    const translation = preferredTranslation || englishTranslation;

    const episode: UnifiedEpisodeWithTranslation = {
      ...data,
      title: translation?.title || `Episode ${data.episode_position}`,
      description: translation?.description || null,
      language_used: translation?.language_code || 'default'
    };

    this.setCache(cacheKey, episode);
    return episode;
  }

  async getFirstEpisodesOfAllSeries(
    languageCode: string = 'en',
    seriesIds?: number[]
  ): Promise<Map<number, UnifiedEpisodeWithTranslation>> {
    if (!supabase) return new Map();

    let query = supabase
      .from('unified_contents_series_episodes')
      .select(`
        episode_id,
        series_id,
        season_id,
        season_position,
        episode_position,
        duration,
        product_year,
        url_streaming_no_drm,
        url_streaming_drm,
        created_at,
        updated_at
      `)
      .eq('season_position', 1)
      .eq('episode_position', 1);

    if (seriesIds && seriesIds.length > 0) {
      query = query.in('series_id', seriesIds);
    }

    const { data: episodes, error } = await query;

    if (error || !episodes) {
      console.error('Error fetching first episodes:', error);
      return new Map();
    }

    const episodeIds = episodes.map(e => e.episode_id);

    const { data: translations } = await supabase
      .from('unified_contents_series_episodes_translations')
      .select('episode_id, title, description, language_code')
      .in('episode_id', episodeIds)
      .in('language_code', [languageCode, 'en']);

    const translationMap = new Map<number, { preferred?: any; english?: any }>();
    translations?.forEach(t => {
      const existing = translationMap.get(t.episode_id) || {};
      if (t.language_code === languageCode) {
        existing.preferred = t;
      } else if (t.language_code === 'en') {
        existing.english = t;
      }
      translationMap.set(t.episode_id, existing);
    });

    const result = new Map<number, UnifiedEpisodeWithTranslation>();

    episodes.forEach(ep => {
      const trans = translationMap.get(ep.episode_id);
      const translation = trans?.preferred || trans?.english;

      result.set(ep.series_id, {
        ...ep,
        title: translation?.title || `Episode ${ep.episode_position}`,
        description: translation?.description || null,
        language_used: translation?.language_code || 'default'
      });
    });

    return result;
  }

  async searchEpisodes(
    query: string,
    languageCode: string = 'en',
    limit: number = 20
  ): Promise<UnifiedSearchResult[]> {
    if (!supabase || !query.trim()) return [];

    const searchTerm = query.trim().toLowerCase();

    const { data, error } = await supabase
      .from('unified_contents_series_episodes_translations')
      .select(`
        episode_id,
        title,
        description,
        language_code,
        unified_contents_series_episodes!inner (
          series_id,
          season_position,
          episode_position,
          duration
        )
      `)
      .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
      .eq('language_code', languageCode)
      .limit(limit);

    if (error) {
      console.error('Error searching episodes:', error);
      return [];
    }

    const results: UnifiedSearchResult[] = (data || []).map((item: any) => ({
      episode_id: item.episode_id,
      series_id: item.unified_contents_series_episodes.series_id,
      title: item.title,
      description: item.description,
      season_position: item.unified_contents_series_episodes.season_position,
      episode_position: item.unified_contents_series_episodes.episode_position,
      duration: item.unified_contents_series_episodes.duration
    }));

    return results;
  }

  async getEpisodeCount(seriesId: number): Promise<number> {
    if (!supabase) return 0;

    const cacheKey = this.getCacheKey('count', seriesId);
    const cached = this.getFromCache<number>(cacheKey);
    if (cached !== null) return cached;

    const { count, error } = await supabase
      .from('unified_contents_series_episodes')
      .select('*', { count: 'exact', head: true })
      .eq('series_id', seriesId);

    if (error) {
      console.error('Error counting episodes:', error);
      return 0;
    }

    const result = count || 0;
    this.setCache(cacheKey, result);
    return result;
  }

  async getSeasonIds(seriesId: number): Promise<number[]> {
    if (!supabase) return [];

    const cacheKey = this.getCacheKey('seasons', seriesId);
    const cached = this.getFromCache<number[]>(cacheKey);
    if (cached) return cached;

    const { data, error } = await supabase
      .from('unified_contents_series_episodes')
      .select('season_id, season_position')
      .eq('series_id', seriesId)
      .order('season_position', { ascending: true });

    if (error || !data) {
      console.error('Error fetching season IDs:', error);
      return [];
    }

    const uniqueSeasons = [...new Set(data.map(d => d.season_id))];
    this.setCache(cacheKey, uniqueSeasons);
    return uniqueSeasons;
  }

  isEpisodeFree(
    episodePosition: number,
    seasonPosition: number,
    freeEpisodesCount: number = 3
  ): boolean {
    if (seasonPosition > 1) return false;
    return episodePosition <= freeEpisodesCount;
  }

  async getEpisodesWithFreeStatus(
    seriesId: number,
    languageCode: string = 'en',
    freeEpisodesCount: number = 3
  ): Promise<(UnifiedEpisodeWithTranslation & { isFree: boolean })[]> {
    const episodes = await this.getEpisodesBySeries(seriesId, languageCode);

    return episodes.map(ep => ({
      ...ep,
      isFree: this.isEpisodeFree(ep.episode_position, ep.season_position, freeEpisodesCount)
    }));
  }

  async getAllEpisodesRaw(): Promise<UnifiedEpisode[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('unified_contents_series_episodes')
      .select('*');

    if (error) {
      console.error('Error fetching all episodes:', error);
      return [];
    }

    return data || [];
  }

  async getTranslationsForEpisode(episodeId: number): Promise<UnifiedEpisodeTranslation[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('unified_contents_series_episodes_translations')
      .select('*')
      .eq('episode_id', episodeId);

    if (error) {
      console.error('Error fetching translations:', error);
      return [];
    }

    return data || [];
  }

  async getAvailableLanguages(): Promise<string[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('unified_contents_series_episodes_translations')
      .select('language_code')
      .limit(1000);

    if (error) {
      console.error('Error fetching languages:', error);
      return [];
    }

    const uniqueLanguages = [...new Set((data || []).map(d => d.language_code))];
    return uniqueLanguages.sort();
  }

  async fetchAllEpisodesForMultipleSeries(
    seriesIds: number[],
    languageCode: string = 'en',
    freeEpisodesCount: number = 3
  ): Promise<Map<number, (UnifiedEpisodeWithTranslation & { isFree: boolean })[]>> {
    const startTime = Date.now();
    const episodesMap = new Map<number, (UnifiedEpisodeWithTranslation & { isFree: boolean })[]>();

    if (!supabase || seriesIds.length === 0) {
      return episodesMap;
    }

    try {
      console.log(`[UNIFIED-BULK] ========================================`);
      console.log(`[UNIFIED-BULK] Starting bulk episode fetch for ${seriesIds.length} series`);
      console.log(`[UNIFIED-BULK] Language: ${languageCode}, Free episodes: ${freeEpisodesCount}`);
      console.log(`[UNIFIED-BULK] ========================================`);

      const episodesData = await this.fetchAllRowsPaginated<any>(
        () => supabase
          .from('unified_contents_series_episodes')
          .select(`
            episode_id,
            series_id,
            season_id,
            season_position,
            episode_position,
            duration,
            product_year,
            url_streaming_no_drm,
            url_streaming_drm,
            created_at,
            updated_at
          `)
          .in('series_id', seriesIds)
          .order('series_id', { ascending: true })
          .order('season_position', { ascending: true })
          .order('episode_position', { ascending: true }),
        `episodes for ${seriesIds.length} series`
      );

      if (!episodesData || episodesData.length === 0) {
        console.log('[UNIFIED-BULK] No episodes found for the given series');
        return episodesMap;
      }

      const uniqueSeriesInData = new Set(episodesData.map(e => e.series_id));
      console.log(`[UNIFIED-BULK] Episodes fetched: ${episodesData.length} total across ${uniqueSeriesInData.size} series`);

      const episodeIds = episodesData.map(e => e.episode_id);
      console.log(`[UNIFIED-BULK] Fetching translations for ${episodeIds.length} episodes (languages: ${languageCode}, en)...`);

      const translations = await this.fetchInBatches<any>(
        episodeIds,
        (batchIds) => supabase
          .from('unified_contents_series_episodes_translations')
          .select('episode_id, title, description, language_code')
          .in('episode_id', batchIds)
          .in('language_code', [languageCode, 'en']),
        `translations for ${episodeIds.length} episodes`
      );

      const translationMap = new Map<number, { preferred?: any; english?: any }>();
      let preferredCount = 0;
      let englishCount = 0;

      (translations || []).forEach(t => {
        const existing = translationMap.get(t.episode_id) || {};
        if (t.language_code === languageCode) {
          existing.preferred = t;
          preferredCount++;
        } else if (t.language_code === 'en') {
          existing.english = t;
          englishCount++;
        }
        translationMap.set(t.episode_id, existing);
      });

      console.log(`[UNIFIED-BULK] Translations loaded: ${preferredCount} in ${languageCode}, ${englishCount} in en`);

      const episodesBySeriesId = new Map<number, typeof episodesData>();
      episodesData.forEach(episode => {
        const seriesId = episode.series_id;
        if (!episodesBySeriesId.has(seriesId)) {
          episodesBySeriesId.set(seriesId, []);
        }
        episodesBySeriesId.get(seriesId)!.push(episode);
      });

      let usingPreferredLang = 0;
      let usingEnglishFallback = 0;
      let usingDefaultFallback = 0;

      episodesBySeriesId.forEach((episodes, seriesId) => {
        const transformedEpisodes = episodes.map((ep) => {
          const trans = translationMap.get(ep.episode_id);
          const translation = trans?.preferred || trans?.english;

          if (trans?.preferred) {
            usingPreferredLang++;
          } else if (trans?.english) {
            usingEnglishFallback++;
          } else {
            usingDefaultFallback++;
          }

          return {
            ...ep,
            title: translation?.title || `Episode ${ep.episode_position}`,
            description: translation?.description || null,
            language_used: translation?.language_code || 'default',
            isFree: this.isEpisodeFree(ep.episode_position, ep.season_position, freeEpisodesCount)
          };
        });

        episodesMap.set(seriesId, transformedEpisodes);
      });

      const duration = Date.now() - startTime;
      const totalEpisodes = Array.from(episodesMap.values()).reduce((sum, eps) => sum + eps.length, 0);

      console.log(`[UNIFIED-BULK] ========================================`);
      console.log(`[UNIFIED-BULK] BULK FETCH COMPLETED`);
      console.log(`[UNIFIED-BULK] Series requested: ${seriesIds.length}`);
      console.log(`[UNIFIED-BULK] Series with episodes: ${episodesMap.size}`);
      console.log(`[UNIFIED-BULK] Total episodes: ${totalEpisodes}`);
      console.log(`[UNIFIED-BULK] Translations used:`);
      console.log(`[UNIFIED-BULK]   - ${languageCode}: ${usingPreferredLang} episodes`);
      console.log(`[UNIFIED-BULK]   - en (fallback): ${usingEnglishFallback} episodes`);
      console.log(`[UNIFIED-BULK]   - default: ${usingDefaultFallback} episodes`);
      console.log(`[UNIFIED-BULK] Total time: ${duration}ms`);
      console.log(`[UNIFIED-BULK] ========================================`);

      const seriesWithoutEpisodes = seriesIds.filter(id => !episodesMap.has(id));
      if (seriesWithoutEpisodes.length > 0) {
        console.warn(`[UNIFIED-BULK] WARNING: ${seriesWithoutEpisodes.length} series have no episodes:`, seriesWithoutEpisodes);
      }

      return episodesMap;
    } catch (err: any) {
      const duration = Date.now() - startTime;
      console.error(`[UNIFIED-BULK] Error fetching episodes (${duration}ms):`, err);
      return episodesMap;
    }
  }

  async getEpisodeByIdSimple(episodeId: number): Promise<{
    episode_id: number;
    series_id: number;
    season_id: number;
    season_position: number;
    episode_position: number;
    duration: number | null;
    url_streaming_no_drm: string | null;
    url_streaming_drm: string | null;
  } | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('unified_contents_series_episodes')
      .select(`
        episode_id,
        series_id,
        season_id,
        season_position,
        episode_position,
        duration,
        url_streaming_no_drm,
        url_streaming_drm
      `)
      .eq('episode_id', episodeId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching episode by id:', error);
      return null;
    }

    return data;
  }
}

export const unifiedContentService = new UnifiedContentService();
